/**
 * PlatformerMode
 *
 * Default gameplay mode: 2.5D platformer with spline-locked movement.
 * Player moves forward/backward along the spline tangent, with Y for jumping.
 * Position is snapped to the spline in XZ.
 */

import { Vector3 } from '@babylonjs/core';
import type { SplineConstraint, SplineProjection } from '../SplineConstraint';
import type { IGameplayMode } from './IGameplayMode';
import type {
  MovementInput,
  MovementResult,
  ConstraintResult,
  GameplayModeContext
} from '../../types/gameplay';

export class PlatformerMode implements IGameplayMode {
  readonly id = 'platformer' as const;
  readonly displayName = 'Platformer';
  readonly allowsInput = true;
  readonly usesVerticalInput = false;

  updateMovement(
    input: MovementInput,
    ctx: GameplayModeContext,
    _deltaTime: number
  ): MovementResult {
    const { path, velocity, moveSpeed } = ctx;
    const spline = path.constraint;

    // Project current position to get tangent
    const { projection } = spline.project(ctx.position, ctx.lastClosestIndex);
    const tangent = projection.tangent.normalize();

    // Calculate desired velocity along tangent
    const desiredVelocity = new Vector3(velocity.x, velocity.y, velocity.z);
    const inputMagnitude = Math.abs(input.horizontal);

    if (inputMagnitude > 0.01) {
      // Move along tangent in input direction
      const direction = Math.sign(input.horizontal);
      const speed = inputMagnitude * moveSpeed;

      desiredVelocity.x = tangent.x * speed * direction;
      desiredVelocity.z = tangent.z * speed * direction;

      return {
        velocity: desiredVelocity,
        facingDirection: direction
      };
    }

    // No horizontal input - stop horizontal movement
    desiredVelocity.x = 0;
    desiredVelocity.z = 0;

    return {
      velocity: desiredVelocity,
      facingDirection: 0 // Preserve current facing
    };
  }

  constrainPosition(
    position: Vector3,
    ctx: GameplayModeContext,
    projection: SplineProjection
  ): ConstraintResult {
    // Snap XZ to spline, preserve Y (handled by physics/grounding)
    const constrainedPos = new Vector3(
      projection.splinePoint.x,
      position.y,
      projection.splinePoint.z
    );

    return {
      position: constrainedPos,
      closestIndex: ctx.lastClosestIndex
    };
  }

  getTargetRotation(
    movementDirection: number,
    spline: SplineConstraint,
    t: number
  ): number {
    // Get tangent at current T
    const point = spline.getPointAt(t);
    const { projection } = spline.project(point, 0);
    const tangent = projection.tangent;

    // Face along tangent in movement direction
    const facingTangent = movementDirection >= 0 ? tangent : tangent.negate();
    return Math.atan2(facingTangent.x, facingTangent.z);
  }
}
